import React, { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import Page from 'layout/access-management/page'
import PageTitle from 'layout/page-title'
import { split } from 'lodash'
import UserContext from 'context/user-context'
import { useRole, useUpdateRole } from 'api/hooks/use-roles'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Modal from 'react-bootstrap/Modal'
import Badge from 'react-bootstrap/Badge'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import { ROLE_TYPE_OPTIONS, roleTypeLabel } from './RoleTypeSettings'
import Tabs from 'duties/shared/tabs'
import EmptyPage from 'layout/access-management/empty-page'
import ConflictsTab from './conflicts'
import RemediationModal from './RemediationModal'
import EmployeesTab from './employees-tab'
import PermissionsTab from './permissions-tab'
import Stats from './stats'
import AuditLogTab from './audit-log-tab'
import BusinessRules from './BusinessRulesTab'
import DataAccessTab from './DataAccessTab'
import AiDescription from './AiDescription'
import CompareRoles from './CompareRoles'
import ChangePermission from './ChangePermission'
import RoleActionsDropdown from './RoleActionsDropdown'

function Roles() {
  const path = window.location.pathname
  const user = useContext(UserContext)
  // The AI description is gated on the municipality's ai_search_enabled setting.
  const aiEnabled = user?.municipality?.setting?.ai_search_enabled ?? false
  const [activeTab, setActiveTab] = useState('conflicts')
  const [roleModalShow, setRoleModalShow] = useState('')

  const roleId = split(path, '/')[2]

  // Tab filter state lives here so the Actions dropdown exports reflect the
  // filters currently applied on the Users and Permissions tabs.
  const urlParams = new URLSearchParams(window.location.search)
  const defaultEmployeesParams = {
    role_id: roleId,
    page: 1,
    perPage: 25,
    active: true
  }
  const defaultPermissionsParams = {
    role_id: roleId,
    is_high_risk: false,
    is_super_user: false,
    is_expansive: true,
    category: '',
    page: 1,
    perPage: 25
  }
  const [employeesParams, setEmployeesParams] = useState(defaultEmployeesParams)
  const [permissionsParams, setPermissionsParams] = useState({
    ...defaultPermissionsParams,
    is_high_risk: urlParams.get('is_high_risk') === 'true',
    is_super_user: urlParams.get('is_super_user') === 'true',
    category: urlParams.get('category') || ''
  })

  const showPermissions = (overrides) => {
    setPermissionsParams({ ...defaultPermissionsParams, ...overrides })
    setActiveTab('permissions')
  }

  const params = {
    id: roleId
  }

  const { isSuccess, isLoading, data: role } = useRole({ params })
  const updateRole = useUpdateRole()
  const [showTypeModal, setShowTypeModal] = useState(false)
  const [pendingType, setPendingType] = useState('')
  const [pendingPurpose, setPendingPurpose] = useState('')

  const openTypeModal = () => {
    setPendingType(role?.role_type || '')
    setPendingPurpose(role?.purpose || '')
    setShowTypeModal(true)
  }

  const handleSaveType = () => {
    updateRole.mutate(
      {
        roleId: role.id,
        role_type: pendingType || null,
        purpose: pendingPurpose.trim()
      },
      {
        onSuccess: () => {
          toast.success('Role saved')
          setShowTypeModal(false)
        },
        onError: () => toast.error('Could not save role')
      }
    )
  }

  if (isLoading || !isSuccess) {
    return <EmptyPage />
  }

  const isDeleted = role.status === 'Deleted'

  const crumbs = [
    { name: 'Access Management', url: 'duties' },
    { name: 'Roles', url: 'roles' },
    { name: role.name, url: '' }
  ]

  return (
    <Page title={isSuccess ? role.name : ''} pageTitle={'Role'} subtitle="">
      <PageTitle breadcrumbs={crumbs} />
      <div className="d-flex justify-content-between align-items-start mt-4 mb-2">
        <div>
          <div className="d-flex align-items-center gap-2">
            <h4 className="mb-0">{role.name}</h4>
            {role.role_type && (
              <Badge bg="white" text="primary" className="border border-light">
                {roleTypeLabel(role.role_type)}
              </Badge>
            )}
          </div>
          {role.purpose && (
            <div
              className="text-muted small mt-1"
              style={{ whiteSpace: 'pre-wrap' }}
            >
              <strong>Purpose:</strong> {role.purpose}
            </div>
          )}
        </div>
        <div>
          <RoleActionsDropdown
            role={role}
            onManageRole={openTypeModal}
            setRoleModalShow={setRoleModalShow}
            employeesParams={employeesParams}
            permissionsParams={permissionsParams}
          />
          {isSuccess && (
            <CompareRoles
              role={role}
              roleId={roleId}
              show={roleModalShow === 'compare-roles'}
              setShow={setRoleModalShow}
            />
          )}
          {isSuccess && (
            <ChangePermission
              role={role}
              roleId={roleId}
              show={roleModalShow === 'change-permission'}
              setShow={setRoleModalShow}
            />
          )}
          {isSuccess && (
            <RemediationModal
              role={role}
              show={roleModalShow === 'remediation'}
              setShow={setRoleModalShow}
            />
          )}
        </div>
      </div>

      <Modal
        show={showTypeModal}
        onHide={() => setShowTypeModal(false)}
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage Role</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group as={Row} className="align-items-center">
            <Form.Label column sm={4} className="mb-0">
              Classify role as
            </Form.Label>
            <Col sm={8}>
              <Form.Select
                value={pendingType}
                onChange={(e) => setPendingType(e.target.value)}
              >
                <option value="">Select</option>
                {ROLE_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
          </Form.Group>
          {aiEnabled && (
            <div className="mt-4 pt-3 border-top">
              <AiDescription role={role} />
            </div>
          )}
          <Form.Group className="mt-4 pt-3 border-top">
            <Form.Label>Purpose of Role</Form.Label>
            <Form.Control
              as="textarea"
              rows={5}
              value={pendingPurpose}
              onChange={(e) => setPendingPurpose(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="light"
            className="text-primary"
            onClick={() => setShowTypeModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveType}
            disabled={updateRole.isLoading}
          >
            {updateRole.isLoading ? 'Saving...' : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>

      {!isDeleted && (
        <div className="mb-4">
          <Stats
            role={role}
            setActiveTab={setActiveTab}
            showPermissions={showPermissions}
          />
        </div>
      )}

      <Row className="mt-4">
        <Col sm={12}>
          <div className="d-flex mb-3">
            <Tabs
              tabs={[
                { name: 'Internal Conflicts', value: 'conflicts' },
                { name: 'Users', value: 'employees' },
                { name: 'Permissions', value: 'permissions' },
                { name: 'Data Access', value: 'data-access' },
                { name: 'Business Rules', value: 'business-rules' },
                { name: 'Audit Log', value: 'audit-log' }
              ]}
              activeTab={activeTab}
              setActiveTab={setActiveTab}
            />
          </div>
        </Col>
      </Row>

      {activeTab === 'conflicts' && <ConflictsTab role={role} />}

      {activeTab === 'employees' && (
        <EmployeesTab params={employeesParams} setParams={setEmployeesParams} />
      )}

      {activeTab === 'permissions' && (
        <PermissionsTab
          role={role}
          params={permissionsParams}
          setParams={setPermissionsParams}
        />
      )}

      {activeTab === 'audit-log' && <AuditLogTab role={role} />}

      {activeTab === 'business-rules' && <BusinessRules role={role} />}

      {activeTab === 'data-access' && <DataAccessTab roleId={role.id} />}
    </Page>
  )
}

export default Roles
